import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { IVehicleOnlineStatus } from "@app/main/components/videos/models/IVehicleOnlineStatus";
import { CameraStatusService } from "@app/main/components/videos/services/camera-status.service";
import { DateService } from "@app/services/date.service";
import { IVehicle } from "@interfaces/IVehicle";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslationService } from "@services/translation.service";
import { Subscription } from "rxjs";
import "./vehicle-online-status.component.scss";

@UntilDestroy()
@Component({
    selector: "vehicle-online-status",
    template: require("./vehicle-online-status.component.html")
})
export class VehicleOnlineStatusComponent implements OnChanges {

    @Input() vehicle: IVehicle;
    @Input() live: boolean;
    @Input() showContext: boolean;

    @Output() statusChange = new EventEmitter<IVehicleOnlineStatus>();

    get online(): boolean {
        return this.vehicle?.onlineStatus?.online;
    }

    get hasStreaming(): boolean {
        return this.vehicle?.onlineStatus?.hasStreaming;
    }

    // --

    displayText: string;

    // --

    onlineStatusSubscription: Subscription;

    constructor(
        private readonly dateService: DateService,
        private readonly translationService: TranslationService,
        private readonly cameraStatusService: CameraStatusService) {
    }

    ngOnInit(): void {
        this.subscribeToOnlineStatus();
        this.subscribeToForceOnlineStatus();
        this.render();
    }

    ngOnDestroy(): void {
        this.onlineStatusSubscription?.unsubscribe();
        this.onlineStatusSubscription = null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["vehicle"]) {
            this.render();
            this.subscribeToOnlineStatus();
        }

        if (this.live && this.vehicle) {
            this.subscribeToOnlineStatus();
        }
    }

    // ---

    private subscribeToOnlineStatus(): void {
        this.onlineStatusSubscription?.unsubscribe();

        if (!this.live) {
            return;
        }

        this.onlineStatusSubscription = this.cameraStatusService
            .createOnlineStatusObservable(this.vehicle)
            .subscribe((data) => {
                this.vehicle.Plot = data.plot;
                this.vehicle.videoChannels = data.streamingChannels
                this.vehicle.onlineStatus = data.onlineStatus

                this.render();
            });
    }

    private render(): void {
        this.displayText = this.translationService.t(`VehicleOnlineStatus.CameraOffline`);

        if (!this.vehicle) {
            this.displayText = this.translationService.t(`VehicleOnlineStatus.UnknownVehicle`);
            return;
        }

        if (!this.vehicle.onlineStatus) {
            return;
        }

        if (this.live) {
            this.emitUpdatedStatus(this.vehicle.onlineStatus);
        }

        if (this.vehicle.onlineStatus.online) {
            this.displayText = this.translationService.t(`VehicleOnlineStatus.CameraOnline`);
            return;
        }

        if (this.vehicle.onlineStatus.lastOnlineTime) {
            const lastOnlineTime = this.dateService.relativeTime(this.vehicle.onlineStatus.lastOnlineTime);
            this.displayText = lastOnlineTime;

            if (this.showContext) {
                this.displayText = this.translationService.t("VehicleOnlineStatus.LastOnlineX", lastOnlineTime)
            }

            return;
        }
    }

    private emitUpdatedStatus(newStatus: IVehicleOnlineStatus): void {
        this.statusChange.emit(newStatus);
    }
    
    private subscribeToForceOnlineStatus(): void {
        this.cameraStatusService.forceStatusUpdate$.pipe(untilDestroyed(this)).subscribe(() => {
            this.subscribeToOnlineStatus();
        });
    }
}
